import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Select,
  Text,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import {
  RiAddFill,
  RiArrowGoForwardLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiDownload2Line,
  RiFileTextLine,
  RiRefreshLine,
  RiSubtractLine,
} from "react-icons/ri";
import { ProxyRenderer } from "../DocumentViewer/V1/renderers/ProxyRenderer";
import {
  DocViewerContext,
  DocViewerProvider,
} from "../DocumentViewer/V1/state";
import {
  MAX_ZOOM_LEVEL,
  MIN_ZOOM_LEVEL,
  ROTATION_INCREMENTS,
  setRotation,
  setZoomLevel,
  ZOOM_LEVEL_INCREMENTS,
} from "../DocumentViewer/V1/state/actions";

type DocumentViewerProps = {
  files: Array<{ id: string; name: string; url: string }>;
  selectedFileIndex: number;
  onFileSelect: (index: number) => void;
  onDownload?: (file: { id: string; name: string; url: string }) => void;
};

const DocumentViewerContent: React.FC<DocumentViewerProps> = ({
  files,
  selectedFileIndex,
  onFileSelect,
  onDownload,
}) => {
  const { state, dispatch } = useContext(DocViewerContext);
  const { zoomLevel, rotation } = state;

  // State for ProxyRenderer data
  const [contentType, setContentType] = useState<string>();
  const [documentData, setDocumentData] = useState<ArrayBuffer>();
  const [isLoading, setIsLoading] = useState(false);

  // Handle file download
  const handleDownload = () => {
    if (files && files.length > 0 && selectedFileIndex >= 0) {
      const file = files[selectedFileIndex];
      if (file && file.url) {
        if (onDownload) {
          onDownload(file);
        } else {
          // Default download behavior
          const link = document.createElement("a");
          link.href = file.url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    }
  };

  // Check if we have files to display
  const hasFiles = files && files.length > 0;
  const selectedFile =
    hasFiles && selectedFileIndex >= 0 ? files[selectedFileIndex] : null;

  // Helper function to determine content type from filename
  const getContentTypeFromFilename = (filename: string): string => {
    const extension = filename.toLowerCase().split(".").pop();
    switch (extension) {
      case "heic":
      case "heif":
        return "image/heic";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "gif":
        return "image/gif";
      case "pdf":
        return "application/pdf";
      default:
        return "application/octet-stream";
    }
  };

  // Load document data when selected file changes
  useEffect(() => {
    if (!selectedFile?.url) {
      setDocumentData(undefined);
      setContentType(undefined);
      return;
    }

    setIsLoading(true);

    // Determine content type from filename
    const detectedContentType = getContentTypeFromFilename(selectedFile.name);
    setContentType(detectedContentType);

    // Fetch the file data as ArrayBuffer for ProxyRenderer
    fetch(selectedFile.url)
      .then((response) => {
        // Use response content-type if available, otherwise use detected type
        const responseContentType = response.headers.get("content-type");
        if (responseContentType) {
          const contentTypes = responseContentType.split(";");
          const contentType = contentTypes.length
            ? contentTypes[0]
            : detectedContentType;
          setContentType(contentType);
        }

        return response.arrayBuffer();
      })
      .then((arrayBuffer) => {
        setDocumentData(arrayBuffer);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching document data:", error);
        setIsLoading(false);
        // Fallback: set basic content type for simple image display
        setContentType(detectedContentType);
        setDocumentData(undefined);
      });
  }, [selectedFile]);

  return (
    <Flex
      direction="column"
      flex="3"
      borderRight="1px"
      borderColor="gray.300"
      h="100%"
      overflow="hidden"
    >
      {/* File selector row with controls */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        p={2}
        mb={0}
        borderBottomWidth="1px"
        borderColor="gray.100"
      >
        {/* File icon and dropdown on left side */}
        <Flex flex="1" alignItems="center" pr={4}>
          <Box boxSize="5" mr={2} color="black">
            <Icon as={RiFileTextLine} boxSize="5" />
          </Box>
          <Select
            value={selectedFileIndex}
            onChange={(e) => onFileSelect(Number(e.target.value))}
            size="sm"
            variant="outline"
            fontWeight="medium"
            color="black"
            width="auto"
            bg="white"
            borderColor="gray.300"
            isDisabled={!hasFiles}
            _hover={{
              borderColor: "primary.500",
              boxShadow: "0 0 0 1px #3182ce",
            }}
            _focus={{
              borderColor: "primary.500",
              boxShadow: "0 0 0 1px #3182ce",
            }}
          >
            {hasFiles ? (
              files.map((file, index) => (
                <option key={file.id} value={index}>
                  {file.name}
                </option>
              ))
            ) : (
              <option value="-1">No files available</option>
            )}
          </Select>
        </Flex>

        {/* Navigation controls and page indicator */}
        <Flex alignItems="center" gap={2}>
          <IconButton
            aria-label="Previous file"
            icon={<RiArrowLeftSLine />}
            size="sm"
            variant="solid"
            bg="gray.100"
            color="gray.600"
            borderRadius="md"
            isDisabled={
              !hasFiles || files.length <= 1 || selectedFileIndex <= 0
            }
            onClick={() => {
              if (selectedFileIndex > 0) {
                onFileSelect(selectedFileIndex - 1);
              }
            }}
            _hover={{ color: "primary.500", bg: "gray.200" }}
          />
          <Text fontSize="sm" color="black" fontWeight="semibold">
            {hasFiles
              ? `${selectedFileIndex + 1} of ${files.length}`
              : "0 of 0"}
          </Text>
          <IconButton
            aria-label="Next file"
            icon={<RiArrowRightSLine />}
            size="sm"
            variant="solid"
            bg="gray.100"
            color="gray.600"
            borderRadius="md"
            isDisabled={
              !hasFiles ||
              files.length <= 1 ||
              selectedFileIndex >= files.length - 1
            }
            onClick={() => {
              if (hasFiles && selectedFileIndex < files.length - 1) {
                onFileSelect(selectedFileIndex + 1);
              }
            }}
            _hover={{ color: "primary.500", bg: "gray.200" }}
          />
        </Flex>

        <Box width="16px" />

        {/* Controls on right side */}
        <HStack spacing={2}>
          {/* Zoom controls */}
          <IconButton
            aria-label="Zoom in"
            icon={<RiAddFill />}
            size="sm"
            variant="solid"
            bg="gray.100"
            color="gray.600"
            borderRadius="md"
            isDisabled={zoomLevel >= MAX_ZOOM_LEVEL}
            onClick={() =>
              dispatch(setZoomLevel(zoomLevel + ZOOM_LEVEL_INCREMENTS))
            }
            _hover={{ color: "primary.500", bg: "gray.200" }}
          />
          <Text
            fontSize="sm"
            color="black"
            fontWeight="semibold"
            width="42px"
            textAlign="center"
          >
            {Math.round(zoomLevel * 100)}%
          </Text>
          <IconButton
            aria-label="Zoom out"
            icon={<RiSubtractLine />}
            size="sm"
            variant="solid"
            bg="gray.100"
            color="gray.600"
            borderRadius="md"
            isDisabled={zoomLevel <= MIN_ZOOM_LEVEL}
            onClick={() =>
              dispatch(setZoomLevel(zoomLevel - ZOOM_LEVEL_INCREMENTS))
            }
            _hover={{ color: "primary.500", bg: "gray.200" }}
          />

          <Box width="0px" />

          {/* Rotation controls */}
          <IconButton
            aria-label="Reset rotation"
            icon={<RiRefreshLine />}
            size="sm"
            variant="solid"
            bg="gray.100"
            color="gray.600"
            borderRadius="md"
            onClick={() => dispatch(setRotation(0))}
            _hover={{ color: "primary.500", bg: "gray.200" }}
          />
          <IconButton
            aria-label="Rotate right"
            icon={<RiArrowGoForwardLine />}
            size="sm"
            variant="solid"
            bg="gray.100"
            color="gray.600"
            borderRadius="md"
            onClick={() =>
              dispatch(setRotation((rotation + ROTATION_INCREMENTS) % 360))
            }
            _hover={{ color: "primary.500", bg: "gray.200" }}
          />

          <Box width="0px" />

          {/* Download button */}
          <IconButton
            aria-label="Download"
            icon={<RiDownload2Line />}
            size="sm"
            variant="solid"
            bg="gray.100"
            color="gray.600"
            borderRadius="md"
            isDisabled={!hasFiles || selectedFileIndex < 0}
            onClick={handleDownload}
            _hover={{ color: "primary.500", bg: "gray.200" }}
          />
        </HStack>
      </Flex>

      {/* Document preview area */}
      <Box
        borderWidth="1px"
        borderColor="gray.100"
        bg="gray.50"
        flexGrow="1"
        overflow="hidden"
      >
        <>
          {selectedFile ? (
            <>
              {isLoading ? (
                <Flex alignItems="center" justifyContent="center" height="100%">
                  <Text color="gray.500">Loading document...</Text>
                </Flex>
              ) : documentData && contentType ? (
                <ProxyRenderer
                  contentType={contentType}
                  documentData={documentData}
                  showAllPages={true}
                />
              ) : (
                <Text color="gray.400">Unable to load document</Text>
              )}
            </>
          ) : (
            <Text color="gray.400">
              {!hasFiles
                ? "No documents available"
                : "Select a document to preview"}
            </Text>
          )}
        </>
      </Box>
    </Flex>
  );
};

export const DocumentViewer: React.FC<DocumentViewerProps> = (props) => {
  return (
    <DocViewerProvider>
      <DocumentViewerContent {...props} />
    </DocViewerProvider>
  );
};
